import React, { useState, useEffect } from 'react';
import '../../App.css';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { bookingService, locationService } from '../../api/apiService';

function CreateKindOrder() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [client, setClient] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateInput, setDateInput] = useState('');
  const [locationData, setLocationData] = useState(null); // Store full location object
  const [createAsGroup, setCreateAsGroup] = useState(true); // Whether to group the orders together (default: true)
  const [kindInfo, setKindInfo] = useState(''); // KIND-specific information (KIND orders only)
  const [kpi, setKpi] = useState(''); // KPI (optional, same as normal orders)

  // Fixed values for KIND orders
  const position = { value: 'AI Content Production', label: 'AI Content Production' };
  const studio = { value: 'remote', label: 'Remote' };
  const location = { value: 'kind', label: 'K.I.N.D.' };
  const set = { value: 'digital_production', label: 'digital Production' };

  // Fetch K.I.N.D. location data on mount
  useEffect(() => {
    const fetchKindLocation = async () => {
      try {
        const locationsResponse = await locationService.getAllLocations(1, 1000);
        const locationsData = locationsResponse.data?.locations || [];
        
        // Find the K.I.N.D. location
        const kindLocation = locationsData.find(
          loc => loc.name === 'K.I.N.D.' || loc.short === 'K.I.N.D.' || loc.short === 'KIND'
        );
        
        if (kindLocation) {
          setLocationData(kindLocation);
        }
      } catch (error) {
        console.error('Error fetching K.I.N.D. location:', error);
      }
    };
    
    fetchKindLocation();
  }, []);

  // Client dropdown options
  const clientOptions = [
    { value: 'z_epdp_i_kind', label: 'Z EPDP I KIND.' },
    { value: 'z_pdp_i_kind', label: 'Z PDP I KIND.' },
    { value: 'z_eps_i_kind', label: 'Z EPS I KIND.' },
    { value: 'bogner_i_kind', label: 'Bogner I KIND.' },
    { value: 'bonprix_i_kind', label: 'Bonprix I KIND.' },
    { value: 'hugo_boss_i_kind', label: 'HUGO BOSS I KIND.' },
    { value: 'puma_i_kind', label: 'PUMA I KIND.' },
    { value: 'kind', label: 'K.I.N.D.' },
    { value: 'hfg_i_kind', label: 'HFG I KIND.' },
    { value: 'tom_tailor_i_kind', label: 'Tom Tailor I KIND.' },
    { value: 'dev_ai_character_creation', label: 'DEV - AI Character Creation' },
    { value: 'dev_ai_character_replacement', label: 'DEV - AI Character Replacement' },
    { value: 'dev_ai_background_creation', label: 'DEV - AI Background Creation' },
    { value: 'dev_ai_background_replacement', label: 'DEV - AI Background Replacement' },
    { value: 'dev_image_to_ai_video', label: 'DEV - Image to AI-Video' },
    { value: 'dev_bust_to_on_model', label: 'DEV - Bust to on-Model' },
    { value: 'dev_ai_content_hub', label: 'DEV - AI Content Hub' },
    { value: 'dev_kind', label: 'DEV - KIND' }
  ];

  // Handle adding a date to the selected dates array
  const handleAddDate = () => {
    if (!dateInput) return;
    
    // Check if date already exists
    if (selectedDates.includes(dateInput)) {
      alert('This date is already selected');
      return;
    }
    
    // Add date and sort the array
    const newDates = [...selectedDates, dateInput].sort();
    setSelectedDates(newDates);
    setDateInput(''); // Clear input
  };

  // Handle removing a date
  const handleRemoveDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter(date => date !== dateToRemove));
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('de-DE', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const customStyles = {
    dropdownIndicator: (provided, state) => ({
      ...provided,
      border: 'none',
    }),
    control: (base) => ({
      ...base,
      width: '100%',
      borderColor: 'black',
      borderWidth: '1px',
      boxShadow: 'none',
      margin: '0 0 10px 0',
      borderRadius: '0px',
      color: 'black',
      fontWeight: "700",
      height: '50px',
      textTransform: 'uppercase',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'black',
      fontWeight: "700"
    }),
    input: (provided) => ({
      ...provided,
      color: 'black',
      fontWeight: "700",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'black' : 'inherit',
      '&:hover': { backgroundColor: state.isSelected ? 'black' : '#f7f7f7' }
    }),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!client) {
      alert("Please select a client");
      return;
    }
    
    if (selectedDates.length === 0) {
      alert("Please select at least one date");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      const numberOfDays = selectedDates.length;
      
      // Generate a unique group ID only when grouping is enabled AND there are multiple days
      // (single-day KIND orders are not grouped by default)
      let groupId = null;
      if (createAsGroup && numberOfDays > 1) {
        // Fetch existing bookings to find the highest KIND group number
        const bookingsResponse = await bookingService.getAllBookings(1, 10000);
        const allBookings = bookingsResponse.data?.bookings || [];
        
        // Find all KIND group IDs (format: kind-1, kind-2, etc.)
        const kindGroupNumbers = allBookings
          .filter(booking => booking.group && booking.group.startsWith('kind-'))
          .map(booking => {
            const match = booking.group.match(/^kind-(\d+)$/);
            return match ? parseInt(match[1], 10) : 0;
          })
          .filter(num => !isNaN(num));
        
        // Get the highest number and increment by 1
        const maxGroupNumber = kindGroupNumbers.length > 0 ? Math.max(...kindGroupNumbers) : 0;
        const nextGroupNumber = maxGroupNumber + 1;
        
        groupId = `kind-${nextGroupNumber}`;
        console.log(`Creating KIND group: ${groupId} (previous max: kind-${maxGroupNumber})`);
      }
      
      // Create a separate order for EACH selected date
      const createPromises = selectedDates.map(async (date) => {
        const kindOrderData = {
          title: `KIND Order - ${numberOfDays} day${numberOfDays > 1 ? 's' : ''}`,
          start_date: date, // This specific date
          end_date: date, // Same date (single day order)
          status: "new order", // Default status for new orders
          
          // KIND specific fields
          client: client ? {
            label: client.label,
            value: client.value
          } : null,
          
          // Fixed values for KIND orders
          position: {
            label: position.label,
            value: position.value
          },
          
          studio: studio.label, // Save as string
          location: locationData || location.label, // Save full location object (same as normal bookings)
          set: set.label, // Save as string
          
          kpi: kpi.trim() || 0,
          sonstige_info: additionalInfo || "",
          kind_info: kindInfo.trim() || null,

          created_by: localStorage.getItem("userEmail") || "",
          start_time: "09:00",
          end_time: "18:00",
          
          // Add group ID if grouping is enabled
          ...(groupId && { group: groupId }),
        };
        
        return bookingService.createBooking(kindOrderData);
      });
      
      // Wait for all orders to be created
      const responses = await Promise.all(createPromises);
      
      // Check if all were successful
      if (responses.every(response => response && response.data && response.data.booking)) {
        const groupMessage = groupId ? ' and grouped together' : '';
        alert(`${numberOfDays} KIND Order${numberOfDays > 1 ? 's' : ''} created successfully${groupMessage}!`);
        // Navigate back to all bookings
        navigate('/admin/newallbookings');
      } else {
        throw new Error("Some orders failed to create");
      }
    } catch (error) {
      console.error("Error creating KIND orders:", error);
      const errorMsg = selectedDates.length > 1 
        ? `Error creating ${selectedDates.length} KIND orders: ${error.message}`
        : `Error creating KIND order: ${error.message}`;
      setError(errorMsg);
      alert(`Failed to create KIND order(s): ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="content-wrapper page">
      <section>
        <h1 className='h1'>Create K.I.N.D. Order</h1>
        {error && <div style={{ color: 'red', marginBottom: '20px', padding: '15px', backgroundColor: '#ffebee', borderRadius: '4px' }}>{error}</div>}
        
        <form 
          onSubmit={handleSubmit}
          style={{
            marginTop: '40px',
            maxWidth: '500px'
          }}
        >
          <Select
            options={clientOptions}
            value={client}
            onChange={setClient}
            placeholder="Client"
            styles={customStyles}
            isClearable
          />

          <input
            className="inputtext"
            type="text"
            value="AI Content Production"
            disabled
            placeholder="Position"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />

          <input
            className="inputtext"
            type="text"
            value="Remote"
            disabled
            placeholder="Studio"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />

          <input
            className="inputtext"
            type="text"
            value="K.I.N.D."
            disabled
            placeholder="Location"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />

          <input
            className="inputtext"
            type="text"
            value="digital Production"
            disabled
            placeholder="Set"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />

          <input
            className="inputtext"
            type="text"
            value={kpi}
            onChange={(e) => setKpi(e.target.value)}
            placeholder="KPI (optional)"
          />

          <div style={{ marginTop: '10px' }}>
            <label style={{ fontWeight: '700', marginBottom: '5px', display: 'block' }}>Select Dates</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <div style={{ 
                    position: 'relative', 
                    flex: '1',
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #000',
                    borderRadius: '0px',
                    backgroundColor: '#fff',
                    padding: '10px',
                    cursor: 'pointer',
                    height: '50px'
                  }}
                  onClick={() => document.getElementById('kind-date-input').showPicker()}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ marginRight: '10px', flexShrink: 0 }}
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <input
                      id="kind-date-input"
                      className="inputdate"
                      type="date"
                      value={dateInput}
                      onChange={(e) => setDateInput(e.target.value)}
                      style={{ 
                        flex: '1',
                        border: 'none',
                        outline: 'none',
                        padding: '0',
                        margin: '0',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddDate}
                    className="big-button"
                    style={{ padding: '10px 20px', height: '50px', margin: 0 }}
                  >
                    Add Date
                  </button>
                </div>
          </div>

          {selectedDates.length > 0 && (
            <div style={{ marginTop: '20px' }}>
                  <label style={{ fontWeight: '700', marginBottom: '10px', display: 'block' }}>
                    Selected Dates ({selectedDates.length} day{selectedDates.length > 1 ? 's' : ''})
                  </label>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '10px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    padding: '10px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px'
                  }}>
                    {selectedDates.map((date, index) => (
                      <div 
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        <span style={{ fontWeight: '600' }}>{formatDate(date)}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDate(date)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ff4444',
                            cursor: 'pointer',
                            fontSize: '18px',
                            padding: '0 4px',
                            lineHeight: '1'
                          }}
                          title="Remove date"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                </div>
            </div>
          )}

          <textarea
            className="inputtext"
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            style={{ height: '100px', marginTop: '10px' }}
            placeholder="Additional Information"
          />

          <div style={{ marginTop: '20px' }}>
            <label style={{ fontWeight: '700', marginBottom: '5px', display: 'block' }}>KIND Info</label>
            <textarea
              className="inputtext"
              value={kindInfo}
              onChange={(e) => setKindInfo(e.target.value)}
              style={{ height: '80px', marginTop: '5px' }}
              placeholder="KIND-specific information (optional)"
            />
          </div>

          {/* Group checkbox - only show if multiple dates are selected */}
          {selectedDates.length > 1 && (
            <div style={{ 
                marginTop: '30px', 
                padding: '20px',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600'
                }}>
                  <input
                    type="checkbox"
                    checked={createAsGroup}
                    onChange={(e) => setCreateAsGroup(e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  <span>
                    Group these {selectedDates.length} orders together
                  </span>
                </label>
                <div style={{ 
                  marginTop: '8px', 
                  marginLeft: '32px', 
                  fontSize: '13px', 
                  color: '#666',
                  fontStyle: 'italic'
                }}>
                  All orders will be linked and can be managed as a group
                </div>
            </div>
          )}

          <div style={{ marginTop: '40px' }}>
            <button 
              type="submit" 
              className='big-button-active' 
              disabled={isSubmitting}
              style={{
                padding: '15px 50px',
                fontSize: '16px',
                fontWeight: '700',
                minWidth: '250px',
                backgroundColor: '#000',
                color: '#fff',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create KIND Order'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default CreateKindOrder;
